import { useState, useEffect, useRef, useCallback } from 'react';
import { useBorneStore } from '@/store/borne-store';
import { useConfigStore } from '@/store/config-store';
import { AdminLoginDialog } from '@/components/AdminLoginDialog';

export function ScreensaverScreen() {
  const navigate = useBorneStore((s) => s.navigate);
  const screensaverImages = useConfigStore((s) => s.screensaverImages);

  const [showAdminLogin, setShowAdminLogin] = useState(false);

  // ---- Image carousel state ----
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasImages = screensaverImages.length > 0;

  useEffect(() => {
    if (!hasImages || screensaverImages.length <= 1) return;

    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % screensaverImages.length);
    }, 5000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [hasImages, screensaverImages.length]);

  // ---- Handlers ----

  const handleScreenTap = useCallback(() => {
    if (showAdminLogin) return;
    navigate('products');
  }, [navigate, showAdminLogin]);

  function handleGearClick(e: React.MouseEvent) {
    e.stopPropagation();
    setShowAdminLogin(true);
  }

  function handleAdminSuccess() {
    setShowAdminLogin(false);
    navigate('admin');
  }

  function handleAdminCancel() {
    setShowAdminLogin(false);
  }

  return (
    <div
      className="h-full w-full relative overflow-hidden bg-surface cursor-pointer"
      onClick={handleScreenTap}
    >
      {/* ---- Background ---- */}
      {hasImages ? (
        <div className="absolute inset-0">
          {screensaverImages.map((src, idx) => (
            <img
              key={src}
              src={src}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              style={{ opacity: idx === currentIndex ? 1 : 0 }}
              draggable={false}
            />
          ))}
        </div>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-surface via-surface-light to-surface flex items-center justify-center">
          <span className="text-7xl font-extrabold text-white/10 tracking-[0.3em] select-none">
            SIDEVIEW
          </span>
        </div>
      )}

      {/* ---- Bottom CTA Bar ---- */}
      <div className="absolute bottom-0 left-0 right-0 h-[180px] bg-black/50 flex flex-col items-center justify-center gap-3">
        {/* Hand / touch icon */}
        <svg
          width="80"
          height="80"
          viewBox="0 0 24 24"
          fill="none"
          className="animate-pulse_icon"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C10.3431 2 9 3.34315 9 5V11.5858L7.70711 10.2929C6.7337 9.31948 5.15075 9.31948 4.17735 10.2929C3.20394 11.2663 3.20394 12.8492 4.17735 13.8226L9.35355 19L9.5 19.1464C10.8807 20.5271 12.7957 21.25 14.75 21.25C18.4779 21.25 21.5 18.2279 21.5 14.5V9C21.5 7.34315 20.1569 6 18.5 6C18.1717 6 17.8565 6.05623 17.5628 6.15973C17.1836 4.92017 16.0241 4 14.6667 4C14.183 4 13.7247 4.1156 13.3189 4.32183C12.8955 3.2334 11.8318 2 12 2Z"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="text-2xl text-white font-medium select-none">
          Touchez pour commander
        </span>
      </div>

      {/* ---- Gear icon (admin access) ---- */}
      <button
        type="button"
        onClick={handleGearClick}
        className="absolute top-4 right-4 opacity-30 hover:opacity-60 transition-opacity"
      >
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
            stroke="white"
            strokeWidth="1.5"
          />
          <path
            d="M19.4 15C19.1277 15.6171 19.2583 16.3378 19.73 16.82L19.79 16.88C20.1656 17.2551 20.3766 17.7642 20.3766 18.295C20.3766 18.8258 20.1656 19.3349 19.79 19.71C19.4149 20.0856 18.9058 20.2966 18.375 20.2966C17.8442 20.2966 17.3351 20.0856 16.96 19.71L16.9 19.65C16.4178 19.1783 15.6971 19.0477 15.08 19.32C14.4755 19.5791 14.0826 20.1724 14.08 20.83V21C14.08 22.1046 13.1846 23 12.08 23C10.9754 23 10.08 22.1046 10.08 21V20.91C10.0642 20.2327 9.63587 19.6339 9 19.4C8.38291 19.1277 7.66219 19.2583 7.18 19.73L7.12 19.79C6.74493 20.1656 6.23584 20.3766 5.705 20.3766C5.17416 20.3766 4.66507 20.1656 4.29 19.79C3.91445 19.4149 3.70343 18.9058 3.70343 18.375C3.70343 17.8442 3.91445 17.3351 4.29 16.96L4.35 16.9C4.82167 16.4178 4.95234 15.6971 4.68 15.08C4.42093 14.4755 3.82764 14.0826 3.17 14.08H3C1.89543 14.08 1 13.1846 1 12.08C1 10.9754 1.89543 10.08 3 10.08H3.09C3.76733 10.0642 4.36613 9.63587 4.6 9C4.87234 8.38291 4.74167 7.66219 4.27 7.18L4.21 7.12C3.83445 6.74493 3.62343 6.23584 3.62343 5.705C3.62343 5.17416 3.83445 4.66507 4.21 4.29C4.58507 3.91445 5.09416 3.70343 5.625 3.70343C6.15584 3.70343 6.66493 3.91445 7.04 4.29L7.1 4.35C7.58219 4.82167 8.30291 4.95234 8.92 4.68H9C9.60453 4.42093 9.99764 3.82764 10 3.17V3C10 1.89543 10.8954 1 12 1C13.1046 1 14 1.89543 14 3V3.09C14.0024 3.74764 14.3955 4.34093 15 4.6C15.6171 4.87234 16.3378 4.74167 16.82 4.27L16.88 4.21C17.2551 3.83445 17.7642 3.62343 18.295 3.62343C18.8258 3.62343 19.3349 3.83445 19.71 4.21C20.0856 4.58507 20.2966 5.09416 20.2966 5.625C20.2966 6.15584 20.0856 6.66493 19.71 7.04L19.65 7.1C19.1783 7.58219 19.0477 8.30291 19.32 8.92V9C19.5791 9.60453 20.1724 9.99764 20.83 10H21C22.1046 10 23 10.8954 23 12C23 13.1046 22.1046 14 21 14H20.91C20.2524 14.0024 19.6591 14.3955 19.4 15Z"
            stroke="white"
            strokeWidth="1.5"
          />
        </svg>
      </button>

      {/* ---- Admin Login Dialog ---- */}
      {showAdminLogin && (
        <div onClick={(e) => e.stopPropagation()}>
          <AdminLoginDialog
            onSuccess={handleAdminSuccess}
            onCancel={handleAdminCancel}
          />
        </div>
      )}
    </div>
  );
}
